import { html, nothing, type PropertyDeclarations, type PropertyValues } from "lit";
import { LmaxBaseChartComponent } from "../../shared/components/lmax-base-chart-component";
import { renderChart } from "../chart";
import { fetchLatencyData, fetchLatencyDataFrom } from "../services/LatencyDataService";
import type { LatencyPoint } from "../definitions/LatencyPoint";
import type { LmaxChartLegend } from "./lmax-chart-legend";
import "./lmax-chart-legend";
import type { LmaxChartRangeLegend } from "./lmax-chart-range-legend";
import "./lmax-chart-range-legend";

export class LmaxWebLatencyChart extends LmaxBaseChartComponent {
  // See `LmaxBaseChartComponent.properties`' comment for why this is
  // explicitly typed as `PropertyDeclarations`.
  static properties: PropertyDeclarations = {
    src: { type: String },
    showtooltip: { type: Boolean },
    showZoom: { type: Boolean },
    xAxisLable: { type: String, attribute: "x-axis-lable" },
    yAxisLable: { type: String, attribute: "y-axis-lable" },
    styleHref: { type: String, attribute: "stylehref" },
    _data: { state: true },
    _label: { state: true },
  };

  declare src: string;
  declare showtooltip: boolean;
  declare showZoom: boolean;
  declare xAxisLable: string;
  declare yAxisLable: string;
  declare styleHref: string;
  private declare _data: LatencyPoint[];
  /** Server-supplied period label (e.g. `"Aug 25 - Aug 26"`) for the range-legend badge — see `fetchLatencyDataFrom`. */
  private declare _label: string | null;

  // No `static styles` here — this component's shadow-DOM CSS (everything
  // but the loading/error messages it inherits from `LmaxBaseChartComponent`)
  // is loaded at runtime via `styleHref` below, not bundled at build time.

  constructor() {
    super();
    this.src = "";
    this.showtooltip = false;
    this.showZoom = false;
    this.xAxisLable = "Latency (ms)";
    this.yAxisLable = "Proportion of orders";
    this.styleHref = "";
    this._data = [];
    this._label = null;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.debugLog("connectedCallback", {
      src: this.src || "(configured endpoint)",
      showtooltip: this.showtooltip,
      showZoom: this.showZoom,
    });
    this.load();
  }

  private async load(): Promise<void> {
    const source = this.src || "(configured endpoint)";
    this.debugLog("load:start", { source });
    this._status = "loading";
    try {
      const result = this.src ? await fetchLatencyDataFrom(this.src) : await fetchLatencyData();
      this._data = result.points;
      this._label = result.label;
      this._status = "ready";
      if (this._data.length === 0) {
        // Edge case: request succeeded but returned no points — nothing to
        // plot, distinct from the error path below.
        this.debugLog("load:success — empty dataset", { source });
      } else {
        this.debugLog("load:success", { source, points: this._data.length });
      }
    } catch (error) {
      console.error("[lmax-web-latency-chart] failed to load latency data:", error);
      this.debugLog("load:error", { source, error });
      this._status = "error";
    }
  }

  /**
   * Resolves this instance's line/gradient color from CSS custom
   * properties (see the class doc comment) — `base.css`'s `:root` defaults
   * unless a `[tagId="..."]` rule overrides them for this specific
   * instance.
   */
  private get themeColors(): { lineColor: string; gradientColor: string } {
    return {
      lineColor: this.readCssVar("--latency-line-color"),
      gradientColor: this.readCssVar("--latency-gradient-color"),
    };
  }

  protected updated(changed: PropertyValues): void {
    if (this._status !== "ready") {
      // Edge case: a reactive property changed (e.g. an attribute edited
      // while still loading/errored) but there's no data yet to rebuild
      // against — `updated()` bails without touching the chart.
      this.debugLog("updated — skipped, not ready", { status: this._status, changed: [...changed.keys()] });
      return;
    }

    // Any reactive property (label text, toggles, a freshly-loaded dataset)
    // can affect how the chart should look — rebuild it. `chart.ts` owns
    // its own ResizeObserver internally, so this doesn't run on resize.
    const container = this.shadowRoot?.getElementById("chart");
    if (container) {
      this.debugLog("chart:rebuild", { changed: [...changed.keys()], points: this._data.length });
      this.cleanupChart?.();
      this.cleanupChart = renderChart(container, this._data, {
        xAxisLabel: this.xAxisLable,
        yAxisLabel: this.yAxisLable,
        showTooltip: this.showtooltip,
        showZoom: this.showZoom,
        ...this.themeColors,
      });
    } else {
      // Edge case: status is "ready" but the shadow DOM hasn't painted the
      // `#chart` container yet (e.g. `_status` and the container-bearing
      // template update in the same batch) — nothing to render into this pass.
      this.debugLog("chart:rebuild — skipped, no container", { changed: [...changed.keys()] });
    }

    if (
      changed.has("_data") ||
      changed.has("_status") ||
      changed.has("_label") ||
      changed.has("overrideCssPath")
    ) {
      this.updateLegends();
      this.updateRangeLegends();
    }
  }

  private onSlotChange(): void {
    this.debugLog("slotchange");
    this.updateLegends();
  }

  private onRangeLegendSlotChange(): void {
    this.debugLog("slotchange:range-legend");
    this.updateRangeLegends();
  }

  /**
   * Hands each slotted `<lmax-chart-legend>` the resolved dataset to render
   * against, plus this instance's own `overrideCssPath` — each legend is a
   * separate custom element with its own shadow root, so it can't see the
   * parent's stylesheet on its own and needs the path passed down explicitly.
   */
  private updateLegends(): void {
    if (this._status !== "ready") return;
    // `:not([name])` — the stats-row legends use the default slot; the
    // range-legend badge (see `updateRangeLegends`) uses its own named one,
    // so a bare `querySelector("slot")` would risk matching whichever one
    // happens to come first in the shadow template.
    const slot = this.shadowRoot?.querySelector("slot:not([name])");
    if (!slot) return;

    const assigned = (slot as HTMLSlotElement).assignedElements();
    let matched = 0;
    for (const el of assigned) {
      if (el.tagName.toLowerCase() === "lmax-chart-legend") {
        const legend = el as LmaxChartLegend;
        legend.stats = { data: this._data };
        legend.overrideCssPath = this.overrideCssPath;
        matched++;
      }
      // Edge case: anything else slotted in (not a `<lmax-chart-legend>`)
      // is silently left alone here — it renders via the plain `<slot>`
      // but never receives `stats`/`overrideCssPath`.
    }
    this.debugLog("legends:sync", { slotted: assigned.length, matched, points: this._data.length });
  }

  /**
   * Hands each `slot="range-legend"` element the resolved `overrideCssPath`
   * plus this instance's `_label` (the server-supplied period, e.g.
   * `"Aug 25 - Aug 26"` — see `fetchLatencyDataFrom`) — unlike
   * `updateLegends()` above this doesn't wait on `_status`/`stats`, since the
   * badge falls back to a computed range on its own when there's no label yet.
   */
  private updateRangeLegends(): void {
    const slot = this.shadowRoot?.querySelector("slot[name='range-legend']");
    if (!slot) return;

    const assigned = (slot as HTMLSlotElement).assignedElements();
    let matched = 0;
    for (const el of assigned) {
      if (el.tagName.toLowerCase() === "lmax-chart-range-legend") {
        const rangeLegend = el as LmaxChartRangeLegend;
        rangeLegend.overrideCssPath = this.overrideCssPath;
        rangeLegend.label = this._label;
        matched++;
      }
    }
    this.debugLog("range-legends:sync", { slotted: assigned.length, matched, label: this._label });
  }

  render() {
    return html`
      ${this.styleHref ? html`<link rel="stylesheet" href=${this.styleHref} />` : nothing}
      ${this.cssIncludesResolved}
      ${this.renderStatus(
        "Loading trade data…",
        "Couldn't load trade execution data. Please try again shortly.",
      )}
      <section aria-label="Trade execution latency distribution" ?hidden=${this._status !== "ready"}>
        <header class="stats-row">
          <slot @slotchange=${this.onSlotChange}></slot>
        </header>
        <div class="chart-wrap">
          <div class="chart-container" id="chart"></div>
          <div class="chart-tooltip" role="status" aria-live="polite"></div>
          <slot name="range-legend" @slotchange=${this.onRangeLegendSlotChange}></slot>
        </div>
      </section>
    `;
  }
}

customElements.define("lmax-web-latency-chart", LmaxWebLatencyChart);

/**
 * Same as the spread chart's `prefetchTemplateSpreadCharts`: a chart kept in
 * a `<template>` (cloned in when a popup opens) would otherwise only start
 * fetching on that click. This starts each one's request at page load —
 * its `src`, or the configured endpoint when it has none, exactly as `load()`
 * would — and `LatencyDataService` hands the same request to the chart once
 * it's created. Charts already in the live DOM fetch on connect anyway.
 */
function prefetchTemplateLatencyCharts(): void {
  for (const template of document.querySelectorAll("template")) {
    for (const chart of template.content.querySelectorAll("lmax-web-latency-chart")) {
      const src = chart.getAttribute("src");
      try {
        // Errors are swallowed here: the failed request is evicted from the
        // cache, so the chart retries and reports it when it's opened.
        (src ? fetchLatencyDataFrom(src) : fetchLatencyData()).catch(() => {});
      } catch {
        // A missing endpoint config throws synchronously — the chart
        // reports that itself when it's opened.
      }
    }
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", prefetchTemplateLatencyCharts, { once: true });
} else {
  prefetchTemplateLatencyCharts();
}

declare global {
  interface HTMLElementTagNameMap {
    "lmax-web-latency-chart": LmaxWebLatencyChart;
  }
}
